import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import LocationMap from './components/LocationMap';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import { useTheme } from '../../contexts/ThemeContext';

const ContactUs = () => {
  const { isDarkMode } = useTheme();
  
  useEffect(() => {
    document.title = 'Contact Us - Maayo Fabric Care | Premium Laundry Services in Coimbatore';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 bg-primary">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23e2e8f0%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-foreground/20 rounded-2xl mb-6">
              <Icon name="MessageSquare" size={32} color="var(--color-primary-foreground)" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
              Get in Touch with
              <span className="block text-primary-foreground">Maayo Fabric Care</span>
            </h1>
            
            <p className="text-xl text-primary-foreground font-semibold leading-relaxed mb-8 max-w-3xl mx-auto">
              Have questions about our premium laundry services? Need to schedule a pickup? 
              We're here to help with all your laundry needs in Coimbatore.
            </p>

            {/* Quick Contact Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Clock" size={24} color="var(--color-primary-foreground)" />
                </div>
                <h3 className="font-semibold text-primary-foreground mb-1">Quick Response</h3>
                <p className="text-sm text-primary-foreground font-medium">2-4 hours response time</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="Phone" size={24} color="var(--color-primary-foreground)" />
                </div>
                <h3 className="font-semibold text-primary-foreground mb-1">24/7 Support</h3>
                <p className="text-sm text-primary-foreground font-medium">Emergency line available</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-foreground/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name="MapPin" size={24} color="var(--color-primary-foreground)" />
                </div>
                <h3 className="font-semibold text-primary-foreground mb-1">Local Service</h3>
                <p className="text-sm text-primary-foreground font-medium">Coimbatore coverage</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Contact Form */}
            <div>
              <ContactForm />
            </div>

            {/* Contact Information */}
            <div>
              <ContactInfo />
            </div>
          </div>

          {/* Location Map */}
          <div className="mb-16">
            <LocationMap />
          </div>

          {/* FAQ Section */}
          <div>
            <FAQSection />
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-accent/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Experience Premium Laundry Care?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Join thousands of satisfied customers who trust Maayo Fabric Care for their premium garment care needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('tel:+919600044004')}
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg transition-smooth hover:shadow-elevation-2"
              >
                <Icon name="Phone" size={20} className="mr-3" />
                Call Now for Pickup
              </motion.button> */}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = '/pickup-scheduling'}
                className="inline-flex items-center justify-center px-8 py-4 bg-card text-foreground border-2 border-primary rounded-xl font-semibold text-lg transition-smooth hover:bg-primary hover:text-primary-foreground hover:shadow-elevation-2"
              >
                <Icon name="Calendar" size={20} className="mr-3" />
                Schedule Pickup
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Shirt" size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">Maayo Fabric Care</h3>
                  <p className="text-sm font-inter font-extrabold tracking-brand-tag text-muted-foreground">PREMIUM FABRIC CARE</p>
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Your trusted partner for premium laundry and dry cleaning services in Coimbatore.
                We provide professional garment care with convenient pickup and delivery.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => window.open('https://facebook.com/maayolaundry', '_blank')}
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-smooth"
                >
                  <Icon name="Facebook" size={18} />
                </button>
                <button
                  onClick={() => window.open('https://instagram.com/maayolaundry', '_blank')}
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-smooth"
                >
                  <Icon name="Instagram" size={18} />
                </button>
                <button
                  onClick={() => window.open('https://wa.me/919600044004', '_blank')}
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-smooth"
                >
                  <Icon name="MessageCircle" size={18} />
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-foreground mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => window.location.href = '/home-landing'}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.location.href = '/services-overview'}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    Services
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.location.href = '/pickup-scheduling'}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    Schedule Pickup
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.location.href = '/collection-points'}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    Locations
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.location.href = '/contact-us'}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-foreground mb-4">Contact Info</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" className="mt-1 flex-shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground leading-relaxed">Maayo Fabric Care</p>
                    <p className="leading-relaxed">30/1, Tank Street, Velappanaickenpudur, Chinnavedampatti, Coimbatore – 641049</p>
                    <p className="leading-relaxed mt-1">Location: Thudiyalur to Saravampatti Road, Sunnambu Kalavai bus stop</p>
                  </div>
                </div>
                <a href="tel:+919600044004" className="flex items-center space-x-2 group">
                  <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                  <p className="text-sm text-muted-foreground group-hover:text-primary transition-smooth">96000 44004</p>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8">
            <div className="text-center mb-4">
              <p className="text-muted-foreground">
                © {new Date().getFullYear()} Maayo Fabric Care. All rights reserved. | Premium laundry services in Coimbatore
              </p>
            </div>
            <div className="text-center">
              <p className={`text-xs ${
                isDarkMode ? 'text-slate-400' : 'text-muted-foreground/70'
              }`}>
                Powered by: <a 
                  href="https://thamiratech.in/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`font-medium hover:underline transition-colors ${
                    isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-primary hover:text-primary/80'
                  }`}
                >
                  Thamira Technologies
                </a>  
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;